import type { Message, MessageType } from "./types"

/**
 * Put text on the clipboard, falling back when the modern API is missing or
 * refuses.
 *
 * `navigator.clipboard` needs a secure context and is absent outright in
 * some deployments that still serve plain http (see the invite-link copy in
 * `profile-info-panel.tsx`, which hit the same gap). The `execCommand` path
 * is deprecated but still the only thing that works there: an off-screen
 * textarea holds the text just long enough to select and copy it, then it's
 * removed.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // Some browsers expose the API but refuse it outside a secure context
      // or a user gesture, so fall through to the legacy path below.
    }
  }
  if (typeof document === "undefined") return false
  try {
    const textarea = document.createElement("textarea")
    textarea.value = text
    // Off-screen, not just invisible: a visible one flashes on screen for a
    // frame, and `display: none` is left out of a text selection in some
    // browsers, which would make `execCommand("copy")` copy nothing.
    textarea.style.position = "fixed"
    textarea.style.top = "-1000px"
    textarea.style.left = "-1000px"
    textarea.style.opacity = "0"
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    const ok = document.execCommand("copy")
    document.body.removeChild(textarea)
    return ok
  } catch {
    return false
  }
}

/** Message types whose bubble can show typed text alongside the attachment:
 *  see the `withCaption` wrapper in `message-bubble.tsx`. Location and contact
 *  cards never render one, whatever `text` holds. */
const CAPTIONED_TYPES = new Set<MessageType>(["image", "video", "audio", "file"])

/**
 * Whether a message has text a reader can see and copy: a plain text
 * message, or the caption on an attachment that actually shows one.
 *
 * Excludes a retracted message (nothing it said survives, see `isDeleted` on
 * `Message`), a system pill and a call record (both render as a centered
 * pill, never a bubble with a menu), and an attachment with no caption typed
 * alongside it.
 */
export function hasCopyableText(
  m: Pick<Message, "type" | "text" | "isDeleted" | "isCall" | "isMissedCall">,
): boolean {
  if (m.isDeleted || m.isCall || m.isMissedCall) return false
  if (!m.text || m.text.trim().length === 0) return false
  const type = m.type ?? "text"
  if (type === "text") return true
  return CAPTIONED_TYPES.has(type)
}

/**
 * A message's text, exactly as copying it should read.
 *
 * `TextWithMentionChips` (`message-bubble.tsx`) renders an "@word" token as a
 * styled chip, but it never changes what the word says: `applyMention` in
 * `message-input.tsx` writes `@Name ` straight into the composer, so `m.text`
 * already IS the human-readable form: there is no separate id/token to
 * decode. This is the identity function on purpose, kept as its own export so
 * every call site reads from one place and a future change to how mentions
 * are stored (an encoded token, say) has exactly one spot to update.
 */
export function copyableMessageText(text: string): string {
  return text
}

/** One line of a bulk, WhatsApp-style copy: `[<time>, <date>] <name>: <text>`. */
export interface BulkCopyLine {
  time: string
  date: string
  senderName: string
  text: string
}

/**
 * The calendar date for a bulk-copy line.
 *
 * The thread's own day dividers (`dayLabel` in `conversation-panel.tsx`) say
 * "Today" / "Yesterday" for a recent message, which is right for a divider
 * sitting in the live thread but wrong for a line of text pasted somewhere else
 * and read later: "Today" baked into a copied message is simply false the
 * next day. This always renders a fixed calendar date, in the same shape
 * `dayLabel` itself falls back to once a message is old enough.
 */
export function bulkCopyDate(iso: string | undefined, fallback: string): string {
  if (!iso) return fallback
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return fallback
  return d.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" })
}

/**
 * Join bulk-copy lines in the order given. Callers pass them in thread
 * order, oldest first, the same order `handleMassForward` already forwards
 * in.
 */
export function formatBulkCopyText(lines: BulkCopyLine[]): string {
  return lines.map(l => `[${l.time}, ${l.date}] ${l.senderName}: ${l.text}`).join("\n")
}
